<script>

import { mapState } from "pinia/dist/pinia";
import ImageUpload from "@/components/ImageUpload";
import SelectInput from "@/components/Select/SelectInput.vue";
import { useMainStore } from "@/stores/mainStore";
import { isValidEmailFormat } from "../libs/is-valid-email-format";

export default {
    name: "ServiceDetails",
    components: {
        ImageUpload,
        SelectInput,
    },
    emits: ["update"],
    props: {
        onEditSummaryPage: {
            type: Boolean,
            default: false,
        },
        serviceDetails: {
            type: Object,
            default: () => {
            }, // name, description, primaryCategory, secondaryCategories etc ... (same type of iobj as data below)
        },
    },
    data() {
        return {
            service: {
                name: "",
                description: "",
                img: "",
                primaryCategory: null,
                secondaryCategories: [],
                bookingPolicy: "",
                bookingEmail: "",
                bookingUrl: "",
                imgRaw: {}, /* will be used to store raw image to be saved on the server side */
            },
            errors: {
                name: "",
                description: "",
                img: "",
                primaryCategory: "",
                bookingEmail: "",
            },
            formSubmitted: false,
        };
    },
    methods: {
        validate() { // lm - not quite sure what eslint complaining about, ask the chaps !
            this.service.name === ""
                ? this.errors.name = "Service name is required"
                : this.errors.name = "";
            this.service.description === ""
                ? this.errors.description = "Service description is required"
                : this.errors.description = "";
            this.service.img === ""
                ? this.errors.img = "Image is required"
                : this.errors.img = "";
            this.service.primaryCategory === "" || this.service.primaryCategory === null
                ? this.errors.primaryCategory = "Primary category is required"
                : this.errors.primaryCategory = "";
            this.service.bookingEmail === "" || !isValidEmailFormat(this.service.bookingEmail)
                ? this.errors.bookingEmail = "Booking email is required"
                : this.errors.bookingEmail = "";

            const errors = Object.values(this.errors).filter((el) => el !== "");

            if (errors.length > 0) {
                return false;
            }
            return true;
        },
        setImage(img) {
            this.service.img = img;
        },
    },
    computed: {
        ...mapState(
            useMainStore,
            [
                "allCategories",
                "categories",
            ],
        ),
        descriptionCharLeft() {
            return 1400 - this.service.description.length;
        },

        getActivityImg() {
            return this.imgData.find((images) => images.imgId === this.service.img)?.imgUrl || "";
        },
    },
    watch: {
        service: {
            deep: true,
            handler() {
                this.$emit("update", this.service);
            },
        },
    },
    mounted() {
        // this.categories = this.allCategories;
        this.service = this.serviceDetails;
        // eslint-disable-next-line max-len
        this.service.primaryCategory = this.allCategories.find((obj) => obj.name === this.serviceDetails.primaryCategory);
    },
};
</script>

<template>
    <div class="details">

        <form @submit.prevent="submit">
            <div class="position-relative d-flex flex-row mt-3 header-gap">
                <h2 class="service-title">
                    Service details
                </h2>
            </div>

            <div class="d-flex flex-column">
                <label class="show">Service name<span class="required-field">*</span></label>
                <div class="info-edit-text">This will appear on your listing and in search results.</div>
                <input
                    type="text"
                    v-model="service.name"
                    placeholder="Enter a name"
                    id="activity-name"
                    class="text mt-2"
                    maxlength="45"
                />
                <div v-if="errors.name">
                    <div class="error-text" data-test="validation-error">{{ errors.name }}</div>
                </div>
            </div>

            <div class="mt-3 d-flex flex-column" id="service-description">
                <label class="show">Service description<span class="required-field">*</span></label>
                <textarea v-model="service.description" maxlength="1400" class="" placeholder="Enter a description"
                          rows="3"></textarea>
                <div class="information-text">{{ descriptionCharLeft }} characters left</div>
                <div v-if="errors.description">
                    <div class="error-text" data-test="validation-error">{{ errors.description }}</div>
                </div>
            </div>

            <ImageUpload
                block-title="Service picture"
                @cropped="setImage($event)"
                :main-img="service.imgUrl"
            />
            <div v-if="errors.img">
                <div class="error-text" data-test="validation-error">{{ errors.img }}</div>
            </div>

            <div class="d-flex flex-column mt-3 select-input-container">
                <label class="show">Main category<span class="required-field">*</span></label>
                <SelectInput
                    :options="allCategories"
                    label="name"
                    class="select-category-container mt-2"
                    :default="service.primaryCategory"
                    @change="service.primaryCategory = $event"
                    placeholder="Choose a category"
                    id="main-categories"
                />
                <div v-if="errors.primaryCategory">
                    <div class="error-text" data-test="validation-error">{{ errors.primaryCategory }}</div>
                </div>
            </div>

            <div class="d-flex flex-column mt-3 select-input-container">
                <label class="show">Other category (optional)<span class="required-field">*</span></label>
                <SelectInput
                    :options="allCategories"
                    label="name"
                    class="select-category-container mt-2"
                    :default="service.secondaryCategories"
                    @change="service.secondaryCategories = $event"
                    placeholder="Choose a category"
                    id="secondary-category"
                />
            </div>

            <div class="d-flex flex-column mt-3">
                <label class="show">Booking policy</label>
                <textarea v-model="service.bookingPolicy" maxlength="300" class=""
                          placeholder="Enter your booking policy"
                          rows="3"></textarea>
            </div>

            <h5 class="font-weight-bolder your-details-title">Your details</h5>

            <div class="d-flex flex-column">
                <label class="show">Service email<span class="required-field">*</span></label>
                <div class="info-edit-text">Enquiries will be sent here.</div>
                <input
                    v-model="service.bookingEmail"
                    type="email"
                    placeholder="Enter an email address"
                    class="text mt-2"
                />

                <div v-if="errors.bookingEmail">
                    <div class="error-text" data-test="validation-error">{{ errors.bookingEmail }}</div>
                </div>

                <label class="show mt-3">URL</label>
                <input
                    v-model="service.bookingUrl"
                    type="url"
                    placeholder="Enter a URL"
                    class="text mt-2"
                />
            </div>
        </form>
    </div>
</template>

<style scoped>
.details {
    width: 100%;
    margin: 10px auto;
}

.service-title {
    font-weight: 600;
}

.header-gap {
    margin-bottom: 2.0rem;
}

.info-edit-text {
    font-style: italic;
    font-weight: normal;
    font-size: 0.875rem;
    line-height: 1.3125rem;
    color: #636066;
}

.service-type-title {
    margin-top: 30px;
    margin-bottom: 0px;
    font-style: normal;
    font-weight: 400;
    font-size: 1rem;
    line-height: 1.5rem;
    color: #636066;
}

.your-details-title {
    margin: 26px 0px;
    font-style: normal;
    font-weight: 600;
    font-size: 1.125rem;
    line-height: 1.6875rem;
    color: #272529;
}

.error-text {
    margin-top: 0.5rem;
}

div.select-input-container label {
    padding-bottom: 0;
    margin-bottom: 0;
}

</style>
